/**
 * Owner: Masa Bayu
 * File: pipeline.ts
 * Date:Time: 2026-10-05 14:40
 * Description: Fasa 18+21 · Font Resolver · Text-Plate · real fontSize not bbox.height.
 */

import type { UniversalAtom } from './types';

// ============================================
// KONTRAK DATA INPUT (Dari PDF Extractor)
// ============================================
export interface RawPdfTextItem {
  id: string;
  type: 'text';
  str: string;
  fontName: string;
  fontObj?: { name: string };
  bbox: { x: number; y: number; width: number; height: number };
  /** Glyph size in pt · never substitute multi-line bbox.height. */
  fontSize?: number;
  color?: string;
}

export interface RawPdfImageItem {
  id: string;
  type: 'image' | 'vector';
  assetId: string;
  bbox: { x: number; y: number; width: number; height: number };
}

export type RawPdfItem = RawPdfTextItem | RawPdfImageItem;

/** Alias · public surface used by index.ts */
export type RawPdfAtom = RawPdfItem;

type BBox = { x: number; y: number; width: number; height: number };

// ============================================
// MODUL 1: FONT RESOLVER (Dari Fasa 12)
// ============================================
function resolveFont(
  baseFontName: string,
): { family: string; weight: number; style: 'normal' | 'italic' } {
  if (!baseFontName) {
    return { family: 'sans-serif', weight: 400, style: 'normal' };
  }

  let cleanName = baseFontName;
  let weight = 400;
  let style: 'normal' | 'italic' = 'normal';

  const plusIndex = cleanName.indexOf('+');
  if (plusIndex !== -1) {
    cleanName = cleanName.substring(plusIndex + 1);
  }

  const lowerName = cleanName.toLowerCase();

  if (lowerName.includes('italic') || lowerName.includes('oblique')) {
    style = 'italic';
    cleanName = cleanName.replace(/-?italic/gi, '').replace(/-?oblique/gi, '');
  }

  if (lowerName.includes('black') || lowerName.includes('heavy')) {
    weight = 900;
  } else if (lowerName.includes('extrabold') || lowerName.includes('ultrabold')) {
    weight = 800;
  } else if (lowerName.includes('bold')) {
    weight = 700;
  } else if (lowerName.includes('semibold') || lowerName.includes('demibold')) {
    weight = 600;
  } else if (lowerName.includes('medium')) {
    weight = 500;
  } else if (lowerName.includes('light')) {
    weight = 300;
  } else if (lowerName.includes('thin') || lowerName.includes('hairline')) {
    weight = 100;
  }

  cleanName = cleanName
    .replace(/-?(bold|black|heavy|light|medium|thin|regular|italic|oblique)/gi, '')
    .replace(/^[,-]+|[,-]+$/g, '')
    .trim();

  if (!cleanName) {
    cleanName = 'sans-serif';
  }

  return { family: cleanName, weight, style };
}

// ============================================
// MODUL 2: TEXT-PLATE DETECTOR (Dari Fasa 17)
// ============================================
interface TextPlateResult {
  suppressImageIds: string[];
}

function calculateOverlap(a: BBox, b: BBox): number {
  const xOverlap = Math.max(
    0,
    Math.min(a.x + a.width, b.x + b.width) - Math.max(a.x, b.x),
  );
  const yOverlap = Math.max(
    0,
    Math.min(a.y + a.height, b.y + b.height) - Math.max(a.y, b.y),
  );
  const overlapArea = xOverlap * yOverlap;
  const imgArea = a.width * a.height;
  return imgArea > 0 ? overlapArea / imgArea : 0;
}

function isSimilarSize(
  a: { width: number; height: number },
  b: { width: number; height: number },
): boolean {
  const widthRatio = Math.min(a.width, b.width) / Math.max(a.width, b.width);
  const heightRatio =
    Math.min(a.height, b.height) / Math.max(a.height, b.height);
  return widthRatio >= 0.8 && heightRatio >= 0.8;
}

function detectTextPlates(items: readonly RawPdfItem[]): TextPlateResult {
  const suppressImageIds: string[] = [];
  const images = items.filter(
    (i): i is RawPdfImageItem => i.type === 'image' || i.type === 'vector',
  );
  const texts = items.filter((i): i is RawPdfTextItem => i.type === 'text');

  for (const img of images) {
    for (const txt of texts) {
      const overlap = calculateOverlap(img.bbox, txt.bbox);
      if (overlap >= 0.7 && isSimilarSize(img.bbox, txt.bbox)) {
        suppressImageIds.push(img.id);
        break;
      }
    }
  }

  return { suppressImageIds };
}

/**
 * Glyph size for Face/Classifier.
 * Prefer measured fontSize. Never use multi-line block bbox.height as size
 * (P18 · el_ocr_para_* · 251pt height → giant “A” artifact).
 */
export function resolveTextFontSize(item: RawPdfTextItem): number {
  if (typeof item.fontSize === 'number' && item.fontSize > 0) {
    return item.fontSize;
  }
  const textLen = item.str?.length ?? 0;
  // Tall paragraph plate without explicit size → body default, not block height.
  if (textLen > 32 && item.bbox.height > 40) {
    return 11;
  }
  return item.bbox.height > 0 ? item.bbox.height : 11;
}

// ============================================
// MODUL 3: MAIN PIPELINE (Consolidated)
// ============================================
export function processRawPdfData(
  rawItems: readonly RawPdfItem[],
): UniversalAtom[] {
  const { suppressImageIds } = detectTextPlates(rawItems);
  const atoms: UniversalAtom[] = [];

  for (const item of rawItems) {
    if (suppressImageIds.includes(item.id)) {
      continue;
    }

    if (item.type === 'text') {
      const baseFont = item.fontObj?.name || item.fontName;
      const resolvedFont = resolveFont(baseFont);
      const atom: UniversalAtom = {
        id: item.id,
        type: 'text',
        bbox: item.bbox,
        textContent: item.str,
        font: {
          family: resolvedFont.family,
          weight: resolvedFont.weight,
          style: resolvedFont.style,
          size: resolveTextFontSize(item),
        },
      };
      if (item.color !== undefined) {
        atom.color = item.color;
      }
      atoms.push(atom);
    } else {
      atoms.push({
        id: item.id,
        type: item.type,
        bbox: item.bbox,
        assetId: item.assetId,
        isTextPlate: false,
      });
    }
  }

  return atoms;
}
